import React, { useEffect, useState } from "react";
import { ViewProps } from "../../../../core/components/crud/View";
import { StoryType } from "../../../../models/modelTypes";
import { useFetch } from "../../../../core/components/crud/contexts/fetch.context";
import { useCrud } from "../../../../core/components/crud/contexts/crud.context";
import { useParams } from "react-router-dom";
import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import Error from "../../../../core/components/Error";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Container,
    Grid,
    Link,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import Breadcrumb from "../../../../core/components/Breadcrumb";
import Spinner from "../../../layout/Spinner";
import { ExpandMore } from "@mui/icons-material";
import { VPillPositive } from "../../../../core/components/VPill";
import { useStoryContext } from "../story.context";
import { TabPanel } from "../../../programme/screens/ViewProgramme";
import { date } from "../../../../utils/date";
import { StoryViewHeader } from "../components/StoryViewHeader";

export const ViewStory: React.FC<ViewProps> = (props: ViewProps): JSX.Element => {
    const { labelProperty } = props;
    const { item, fetching, fetchItem } = useFetch<StoryType>();
    const { noun } = useCrud<StoryType>();
    const { setCurrentDetails, setCurrentStory, markStoryReviewed, markStoryPublished } = useStoryContext();
    const [error, setError] = useState<boolean>(false);
    const [tabValue, setTabValue] = useState<number>(0);
    const params = useParams();
    const id = params.id!;
    const { push } = useAppNavigate();
    useEffect(() => {
        if (id && !isNaN(parseInt(id))) {
            fetchItem(parseInt(id))
                .then(() => {
                    setError(false);
                })
                .catch(() => setError(true));
        }
    }, [id]);
    if (error) {
        return <Error description={`Unable to load ${noun}`} />;
    }
    const handleEdit = () => {
        setCurrentStory(item);
        const storyDetails = {
            ...item,
            numParts: item?.StoryParts?.length ?? 0,
            parts:
                item?.StoryParts?.length && item.StoryParts.length > 0
                    ? item.StoryParts.map((part) => {
                          return {
                              part: part.part,
                              content: part.content,
                              figurativeNotes: part.figurativeNotes,
                              imageUrl: part.imageUrl,
                          };
                      })
                    : [],
            themeIds:
                item?.Themes?.length && item.Themes.length > 0
                    ? item.Themes.map((theme) => {
                          return { id: theme.id };
                      })
                    : [],
        };
        setCurrentDetails(storyDetails);
        push("/stories/add");
    };
    const useStyles = makeStyles(() =>
        createStyles({
            accordion: {
                padding: "0 16px 0 16px",
                marginBottom: "16px!important",
                backgroundColor: "rgba(0,0,0,0.05)",
                width: "100%",
            },
            accordionHeader: {
                minHeight: "64px",
            },
            header: {
                backgroundColor: "#F9F9FB",
            },
            textBreak: {
                whiteSpace: "pre-line",
            },
        }),
    );
    const classes = useStyles();
    return (
        <>
            <Box p={3} className={classes.header}>
                <Box mb={2}>
                    <Breadcrumb
                        current={labelProperty && item && item[labelProperty] ? `${item[labelProperty]}` : `${noun}`}
                    />
                </Box>
            </Box>
            {!fetching && item ? (
                <>
                    <Box p={3}>
                        <Container maxWidth={"md"}>
                            <Tabs
                                value={tabValue}
                                onChange={(_event: React.ChangeEvent<unknown>, newValue: number) =>
                                    setTabValue(newValue)
                                }
                            >
                                <Tab label="Details" id="details-tab" />
                                <Tab label="Meta" id="meta-tab" />
                            </Tabs>
                            <TabPanel index={0} value={tabValue}>
                                <Grid container spacing={3}>
                                    <StoryViewHeader
                                        storyName={item.storyName}
                                        onEdit={handleEdit}
                                        onApprove={() => markStoryReviewed(item.id)}
                                        onPublish={() => markStoryPublished(item.id)}
                                        isReviewed={item.isReviewed}
                                        isPublished={item.isPublished}
                                    />
                                    <Grid item xs={12}>
                                        <Box mb={1}>
                                            <Typography variant={"h4"}>Author</Typography>
                                        </Box>
                                        <Typography>{item.author}</Typography>
                                    </Grid>
                                    {item.ageRange && (
                                        <Grid item xs={12} sm={6}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Age Range</Typography>
                                            </Box>
                                            <Typography>{item.ageRange}</Typography>
                                        </Grid>
                                    )}
                                    {item.type && (
                                        <Grid item xs={12} sm={6}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Story Type</Typography>
                                            </Box>
                                            <Typography>
                                                {(item.type as string).replace(/([A-Z])/g, " $1").trim()}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {item.synopsis && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Story Synopsis</Typography>
                                            </Box>
                                            <Typography className={classes.textBreak}>{item.synopsis}</Typography>
                                        </Grid>
                                    )}
                                    {item.overview && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Contextual Overview</Typography>
                                            </Box>
                                            <Typography className={classes.textBreak}>{item.overview}</Typography>
                                        </Grid>
                                    )}
                                    {item.resources && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Additional Resources link - NI</Typography>
                                            </Box>
                                            <Typography
                                                className={classes.textBreak}
                                                component={Link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={item.resources}
                                            >
                                                {item.resources}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {item.resourcesROI && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Additional Resources link ROI</Typography>
                                            </Box>
                                            <Typography
                                                className={classes.textBreak}
                                                component={Link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={item.resourcesROI}
                                            >
                                                {item.resourcesROI}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {item.Themes?.length && item.Themes.length > 0 ? (
                                        <Grid container item xs={12} spacing={1}>
                                            <Grid item xs={12}>
                                                <Box mb={1}>
                                                    <Typography variant={"h4"}>Story Themes</Typography>
                                                </Box>
                                            </Grid>
                                            {item.Themes.map((theme) => (
                                                <Grid item key={theme.id}>
                                                    <VPillPositive label={theme.name} />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : null}
                                    {item.StoryParts?.length &&
                                        item.StoryParts.length > 0 &&
                                        item.StoryParts.map((part) => (
                                            <Accordion
                                                className={classes.accordion}
                                                key={part.id}
                                                elevation={0}
                                                defaultExpanded={true}
                                                style={{ padding: "16px" }}
                                            >
                                                <AccordionSummary
                                                    style={{ padding: "0px" }}
                                                    expandIcon={<ExpandMore />}
                                                    className={classes.accordionHeader}
                                                >
                                                    <Typography variant="h4">Story Part {part.part + 1}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails style={{ height: "100%", padding: "0px" }}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6">Content</Typography>
                                                            <Typography className={classes.textBreak}>
                                                                {part.content}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6">Figurative Notes</Typography>
                                                            <Typography className={classes.textBreak}>
                                                                {part.figurativeNotes}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6">Image URL</Typography>
                                                            <Typography className={classes.textBreak}>
                                                                {part.imageUrl ? part.imageUrl : "None"}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                </Grid>
                            </TabPanel>
                            <TabPanel index={1} value={tabValue}>
                                <Grid container spacing={3}>
                                    {item?.CreateUser?.firstName ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Created By</Typography>
                                            <Typography>{`${item?.CreateUser?.firstName} ${
                                                item?.CreateUser?.lastName ?? ""
                                            } ${
                                                item?.CreateUser?.email ? `/ ${item.CreateUser.email}` : ""
                                            }`}</Typography>
                                        </Grid>
                                    ) : null}
                                    {item?.createdDate ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Created on</Typography>
                                            <Typography>{date(item?.createdDate)}</Typography>
                                        </Grid>
                                    ) : null}
                                    {item?.ReviewUser?.firstName ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Reviewed By</Typography>
                                            <Typography>{`${item?.ReviewUser?.firstName} ${
                                                item?.ReviewUser?.lastName ?? ""
                                            } ${
                                                item?.ReviewUser?.email ? `/ ${item.ReviewUser.email}` : ""
                                            }`}</Typography>
                                        </Grid>
                                    ) : null}
                                    {item?.reviewedDate ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Reviewed on</Typography>
                                            <Typography>{date(item?.reviewedDate)}</Typography>
                                        </Grid>
                                    ) : null}
                                    {item?.PublishUser?.firstName ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Published By</Typography>
                                            <Typography>{`${item?.PublishUser?.firstName} ${
                                                item?.PublishUser?.lastName ?? ""
                                            } ${
                                                item?.PublishUser?.email ? `/ ${item.PublishUser.email}` : ""
                                            }`}</Typography>
                                        </Grid>
                                    ) : null}
                                    {item?.publishedDate ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Published on</Typography>
                                            <Typography>{date(item?.publishedDate)}</Typography>
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </TabPanel>
                        </Container>
                    </Box>
                </>
            ) : (
                <Spinner />
            )}
        </>
    );
};
